/**
 * createSessionConfigHook - Factory for hooks that read tenant/user-scoped
 * configuration once per session and share the result across all consumers.
 *
 * The cache is keyed by the authenticated user's guid (read from the persisted
 * auth slice) so values fetched as one identity are NEVER served to another:
 * - Unauthenticated (no guid): no request is fired and nothing is cached —
 *   consumers get `defaultValue`. Prevents anonymous 401 responses from
 *   poisoning the cache before login.
 * - Ineligible (optional `isEligible` predicate returns false): treated exactly
 *   like unauthenticated — no request, no cache, `defaultValue` for everyone.
 * - Identity change (login / account switch): the previous cache is dropped,
 *   consumers are reset to `defaultValue` immediately, and a fresh fetch runs.
 * - A fetch that resolves after its identity was superseded is discarded.
 *
 * All mounted consumers subscribe to the cache, so a component that mounted
 * before the fetch resolved (or before login) still receives the final values.
 * Within one identity the value is fetched once and lives until full reload
 * (acceptable: tenant-scoped values do not change at runtime within a session).
 */
import { useEffect, useState } from 'react';

import { getPersistedUserGuid } from '../services/serviceConfig';

type SessionConfigHookOptions<T> = {
  /** Performs the request(s) and maps the response to the hook's value. A rejection resolves to `defaultValue`. */
  fetchValue: () => Promise<T>;
  /** Returned while loading, when unauthenticated or ineligible, and on fetch failure. */
  defaultValue: T;
  /** Optional audience gate, re-evaluated on every consumer render. When it returns false, no request is fired and nothing is cached. */
  isEligible?: () => boolean;
};

type SessionCache<T> = {
  identity: string;
  resolved: boolean;
  value: T;
  promise?: Promise<void>;
};

export function createSessionConfigHook<T>({ fetchValue, defaultValue, isEligible = () => true }: SessionConfigHookOptions<T>): () => T {
  let cache: SessionCache<T> | null = null;
  const listeners = new Set<(value: T) => void>();

  function broadcast(value: T): void {
    listeners.forEach((listener) => listener(value));
  }

  function currentValue(): T {
    return cache && cache.resolved && cache.identity === getPersistedUserGuid() && isEligible() ? cache.value : defaultValue;
  }

  function ensureFresh(): void {
    const identity = getPersistedUserGuid();

    if (!identity || !isEligible()) {
      // Logged out or not in this config's audience: drop whatever identity the
      // cache belonged to and never fetch.
      if (cache) {
        cache = null;
        broadcast(defaultValue);
      }
      return;
    }

    if (cache && cache.identity === identity) {
      return; // resolved or in-flight for the current identity
    }

    const hadStaleCache = cache !== null;
    const promise: Promise<void> = fetchValue()
      .catch(() => defaultValue)
      .then((value) => {
        // Commit only if this fetch wasn't superseded by an identity change.
        if (cache && cache.promise === promise) {
          cache = { identity, resolved: true, value };
          broadcast(value);
        }
      });
    cache = { identity, resolved: false, value: defaultValue, promise };

    if (hadStaleCache) {
      // Reset consumers immediately so the previous identity's values are
      // never rendered for the new one while the refetch is in flight.
      broadcast(defaultValue);
    }
  }

  function useSessionConfig(): T {
    const [value, setValue] = useState<T>(currentValue);

    useEffect(() => {
      const listener = (next: T) => setValue(() => next);
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    }, []);

    // Runs after every render: a cheap idempotent revalidation (one memoized
    // storage read) so any consumer render after login/logout/account switch
    // notices the identity change without needing a remount.
    useEffect(() => {
      ensureFresh();
      setValue(() => currentValue());
    });

    return value;
  }

  return useSessionConfig;
}
